import { defineStore, acceptHMRUpdate } from 'pinia'
import { apiClient } from '@/helpers/api.js'
import {
  INVENTORY_CARD_COLUMN_OPTIONS,
  INVENTORY_SORT_OPTIONS,
  AVAILABLE_FILTERS,
  BASE_PARAM_KEYS,
  INVENTORY_CSV_IMPORT,
} from '@/constants'
import { useUserStore } from '@/stores/UserStore'
import { generateCardData } from "@/helpers/card.js"
import { downloadBlob } from '@/helpers/download.js'
import {
  getInventoryStatus,
  checkDismissedDuplication,
  buildInventoryQuery,
  inventoryDefaultColumnOptions,
  normalizeColumnOptionFields,
  mergeColumnOptions,
} from "@/helpers/inventory.js"
import { fetchMarketPricesInto, applyMarketPricesToCardStore } from "@/helpers/marketPrices.js"
import { setToLocalStorageByUserId, getFromLocalStorageByUserId } from '@/helpers/general'
import { handleApiError } from '@/services/errorHandler'
import { useConfigStore } from '@/stores/ConfigStore'
import { useCardStore } from '@/stores/CardStore'

export const useInventoryStore = defineStore('InventoryStore', {
  state: () => {
    return {
      columnOptions: [...INVENTORY_CARD_COLUMN_OPTIONS],
      sortOption: INVENTORY_SORT_OPTIONS[1], // Default: 'Recently added'
      inventoryDetailsLoader: false,
      fieldSearchResult: [],
      isInventory: false,
      nextCardId: null,
      previousCardId: null,
      currentFetchController: null,
      currentMarketPriceController: null,

      views: [],
      viewsLoading: false,
      selectedView: null,
      creatingView: false,
      editingView: false,
      openView: false,
      marketPrices: null,
    }
  },

  getters: {},

  actions: {
    // Initialize sortOption and columnOptions from localStorage
    initFromLocalStorage() {
      const userStore = useUserStore()
      const userId = userStore.user?.id

      // Init sortOption
      const savedSort = getFromLocalStorageByUserId('inventorySort', userId)
      if (savedSort) {
        this.sortOption = savedSort
      }

      // Init columnOptions. Under the new design `defaults` is the unified list
      // (all visible); the merge keeps whatever the user unchecked and adds any
      // column that appeared since — then writes the grown list back.
      const defaults = inventoryDefaultColumnOptions()
      const savedColumns = getFromLocalStorageByUserId('display_columns_inventory', userId)

      if (savedColumns?.length) {
        // Custom fields aren't in `defaults` (they come from config, and DisplayMenu
        // reconciles them) — carry the saved ones through so their visibility isn't
        // dropped on the way.
        const savedCustomFields = savedColumns.filter(col => col.field?.startsWith('custom_fields_'))
        const merged = [...mergeColumnOptions(savedColumns, defaults), ...savedCustomFields]
        this.columnOptions = merged

        // Rewrite only when the set of columns actually changed (compared by
        // field — one added + one dropped leaves the count equal).
        const savedFields = savedColumns.map(col => col.field).join('|')
        const mergedFields = merged.map(col => col.field).join('|')

        if (savedFields !== mergedFields) {
          setToLocalStorageByUserId(merged, 'display_columns_inventory', userId)
        }
      } else {
        this.columnOptions = [...defaults]
      }
    },

    updateToDefault() {
      this.columnOptions = inventoryDefaultColumnOptions()
      this.sortOption = INVENTORY_SORT_OPTIONS[1]
    },

    updateColumnOption(option) {
      const userStore = useUserStore()
      this.columnOptions = this.columnOptions.reduce((acc, item) => {
        item.field === option.field ? acc.push({...item, checked: !item.checked}) :  acc.push(item)

        return acc
      }, [])

      setToLocalStorageByUserId(this.columnOptions, "display_columns_inventory", userStore.user.id)
    },

    updateSortOption(option) {
      const userStore = useUserStore()
      this.sortOption = option
      setToLocalStorageByUserId(option, 'inventorySort', userStore.user.id)
    },

    // ─── CSV import (CDP-2456) ────────────────────────────────────────────────
    // Errors are intentionally re-thrown (no try/catch swallow) so the modal can
    // branch on 403 → Allstar+ upgrade messaging vs. a generic failure.

    /** Download the blank import template CSV. */
    async downloadCsvImportTemplate() {
      const response = await apiClient().get(
        INVENTORY_CSV_IMPORT.ENDPOINTS.TEMPLATE,
        { responseType: 'blob' },
      )
      downloadBlob(response.data, INVENTORY_CSV_IMPORT.TEMPLATE_FILENAME)
    },

    /** Upload a CSV file to the user's default inventory. Resolves the BE payload
     *  `{ ok, job_id, row_count, max_rows }`. */
    async importInventoryCsv(file) {
      const userStore = useUserStore()
      const formData = new FormData()
      formData.append('file', file)

      const { data } = await apiClient(false, true).post(
        INVENTORY_CSV_IMPORT.ENDPOINTS.UPLOAD(userStore.user.default_inventory_id),
        formData,
      )
      return data
    },

    /** Download the row-errors CSV for a failed/partial import job. */
    async downloadCsvImportErrors(jobId) {
      const response = await apiClient().get(
        INVENTORY_CSV_IMPORT.ENDPOINTS.ERRORS(jobId),
        { responseType: 'blob' },
      )
      downloadBlob(response.data, INVENTORY_CSV_IMPORT.ERRORS_FILENAME)
    },

    /** Read a job by id — the CSV import outcome lives in `data`
     *  (`{ status, data: { created, failed, errors[] } }`). Used to confirm the
     *  async import finished before showing results / the error report. */
    async fetchCsvImportJob(jobId) {
      const { data } = await apiClient().get(INVENTORY_CSV_IMPORT.ENDPOINTS.JOB(jobId))
      return data
    },

    async fetchInventory({ id, perPage = 25, isPagination = false, viewId = null }) {
      const cardStore = useCardStore()
      const userStore = useUserStore()

      try {
        if (viewId && !this.views.length) {
          const dataViews = await this.fetchViews()

          this.selectedView = dataViews.find(view => view.id === viewId)
          this.columnOptions = this.selectedView?.columns?.length
            ? normalizeColumnOptionFields(this.selectedView.columns)
            : inventoryDefaultColumnOptions()

          if (this.selectedView?.sort) {
            this.sortOption = this.selectedView.sort
          }
        }
        
        // Cancel previous request if it exists
        if (this.currentFetchController) {
          this.currentFetchController.abort()
        }

        // Create new AbortController for current request
        const controller = new AbortController()
        this.currentFetchController = controller

        cardStore.cardsLoading = true

        if (cardStore.itemFilters.duplicate) {
          // Clear controller before return
          this.currentFetchController = null
          cardStore.hasDuplicates = true

          await this.fetchDuplicates()
          cardStore.duplicationPopup = cardStore.hasDuplicates ? checkDismissedDuplication(userStore.user.id) : false

          cardStore.cards = cardStore.duplicates

          // Apply duplicate market prices if they resolved before the list.
          this.applyMarketPrices()

          return
        }

        const configStore = useConfigStore()  

        // let inventoryList = { data: { available_filters: AVAILABLE_FILTERS } }

        // delete cardStore.itemFilters.status

        // Shared request setup — identical for the inventory list and the
        // market-prices endpoint (see `buildInventoryQuery` / `fetchMarketPrices`).
        const { query, perPage_ } = buildInventoryQuery({
          itemFilters: cardStore.itemFilters,
          page: cardStore.page,
          userId: userStore.user.id,
          perPage,
        })

        const url = `/inventory/${id}${query}`

        // Add signal for request cancellation capability
        const { data } = await apiClient().get(url, {
          signal: controller.signal
        })

        // Check if request was canceled
        if (controller.signal.aborted) {
          return
        }

        if (data?.has_duplicates) {
          cardStore.hasDuplicates = data.has_duplicates
          cardStore.duplicationPopup = cardStore.hasDuplicates ? checkDismissedDuplication(userStore.user.id) : false
          cardStore.duplicatesLength = data.duplicate_count
          this.fetchDuplicates()
        }

        const updatedInventory = data?.items.reduce((acc, item) => {
          item.listing = item.listing || {}
          
          // Merge card and listing data, add status fields
          acc.push({ 
            ...item.card, 
            ...item.listing, 
            status: item.card.status, 
            inventoryStatus: getInventoryStatus(item.card),
            set: item.card.set,
            number: item.card.number,
            title: item.card.title,
            description: item.card.description,
            player: item.card.player,
            team: item.card.team,
            year: item.card.year,
            id: item.card.id,
            quantity: item.card.quantity,
            listingId: item.listing?.id || null,
            collxListedStatus: item.listing?.collx_status,
            ebayListedStatus: item.listing?.ebay_status,
            whatnotListedStatus: item.listing?.whatnot_status,
            shopifyListedStatus: item.listing?.shopify_status,
            sku: item.listing?.sku || item.card?.sku,
            quantity_available: item.listing ? item.listing.quantity_available : 0,
          })
          return acc
        }, [])

        const cards = isPagination 
          ? [...cardStore.cards, ...updatedInventory] 
          : updatedInventory

        const preparedCards = []

        cards.forEach((card, index) => {
          const cardData = generateCardData(card, cardStore.selectedRanges, configStore.config)
          preparedCards.push(cardData)
        })

        cardStore.cards = preparedCards

        // Merge in market prices if they already resolved (the two requests race).
        // Centralised so whichever finishes last applies — see `applyMarketPrices`.
        this.applyMarketPrices()

        cardStore.totalCards = data.total_items_original

        if (data.available_filters?.length || Object.keys(data.available_filters)?.length) {
          cardStore.availableFilters = {
            attribute: [ ...data.available_filters?.attribute ],
            brand: [ ...data.available_filters?.brand ],
            category: [ ...data.available_filters?.category ],
            graded: [ ...data.available_filters?.graded ],
            grader: [ ...data.available_filters?.grader ],
            team: [ ...data.available_filters?.team ],
            status: { ...data.available_filters?.status },
            year: [ ...data.available_filters?.year ],
            box: [ ...data.available_filters?.box ],
            row: [ ...data.available_filters?.row ],
            shelf: [ ...data.available_filters?.shelf ],
            cabinet: [ ...data.available_filters?.cabinet ],
            set: [ ...data.available_filters?.set ],
            batch: [ ...(data.available_filters?.batch || []) ], // CDP-2435 — safe fallback: backend may not return `batch` yet
          }
        }

        cardStore.pagination = {
          perPage: perPage_,
          currentPage: data.current_page,
          totalItems: data.total_items,
          nextPage: data.next_page ? data.current_page + 1 : null,
          prevPage: data.current_page === 1 ? null : data.current_page - 1,
          totalPages: data.total_pages
        }

        setToLocalStorageByUserId(data.total_items, 'inventoryTotalItems', userStore.user?.id)

        cardStore.cardsLoading = false
        
        // Clear controller after successful completion
        this.currentFetchController = null
      } catch (error) {
        // Ignore error if it's a request cancellation
        if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
          return
        }
        
        cardStore.cardsLoading = false
        handleApiError(error, { component: 'InventoryStore', action: 'fetchInventory' })
        
        // Clear controller on error
        this.currentFetchController = null
      }
    },

    async fetchMarketPrices({ id, perPage = 25 }) {
      const cardStore = useCardStore()
      const userStore = useUserStore()

      // Dispatch by mode, mirroring how `fetchInventory` routes to
      // `fetchDuplicates`: duplicate view → the duplicates market-prices
      // endpoint (simple perPage+page query, like `fetchDuplicates`), otherwise
      // the filtered inventory market-prices endpoint (shared query builder).
      let url
      if (cardStore.itemFilters.duplicate) {
        const perPage_ = getFromLocalStorageByUserId('perPage_inventory', userStore.user.id) || perPage
        url = `/inventory/${id}/duplicates/market-prices?perPage=${perPage_}&page=${cardStore.page}`
      } else {
        const { query } = buildInventoryQuery({
          itemFilters: cardStore.itemFilters,
          page: cardStore.page,
          userId: userStore.user.id,
          perPage,
        })
        url = `/inventory/${id}/market-prices${query}`
      }

      // Shared loader: abort-prior + fetch + apply to the card store.
      await fetchMarketPricesInto(this, { url, context: 'InventoryStore' })
    },

    // Merge the current `marketPrices` into whatever inventory is loaded — the
    // list (`cardStore.cards`) and the open detail card. `fetchInventory` calls
    // this when it finishes so recommendations land no matter which request wins
    // the race. Idempotent + a no-op until there are both prices and cards.
    applyMarketPrices() {
      applyMarketPricesToCardStore(this.marketPrices)
    },

    async fetchInventoryById(cardId) {
      if (!cardId) return

      const userStore = useUserStore()
      const configStore = useConfigStore()
      const cardStore = useCardStore()

      try {
        this.inventoryDetailsLoader = true
        const { data } = await apiClient().get(`/inventory/${userStore.user.default_inventory_id}/${cardId}`)
        const card = { 
          ...data.card, 
          ...data.listing, 
          status: data.card.status, 
          inventoryStatus: getInventoryStatus(data.card),
          set: data.card.set,
          number: data.card.number,
          title: data.card.title,
          description: data.card.description,
          player: data.card.player,
          team: data.card.team,
          year: data.card.year,
          id: data.card.id,
          sku: data.card.sku,
          listingId: data.listing?.id || null,
          collxListedStatus: data.listing?.collx_status,
          ebayListedStatus: data.listing?.ebay_status,
          whatnotListedStatus: data.listing?.whatnot_status,
          shopifyListedStatus: data.listing?.shopify_status,
          sku: data.listing?.sku || data.card?.sku,
          quantity_available: data.listing ? data.listing.quantity_available : 0,
          collx_type_id: data.card.collx_type_id,
          sport: data.card.sport,
        }

        cardStore.selectedCardData = generateCardData(card, cardStore.selectedRanges, configStore.config)

        // Apply the market-price recommendation if prices already resolved.
        this.applyMarketPrices()

        this.inventoryDetailsLoader = false

        this.fetchNextPrevIds()
      } catch (error) {
        this.inventoryDetailsLoader = false
        handleApiError(error, { component: 'InventoryStore', action: 'fetchInventoryById' })
      }
    },

    async updatedInventoryCard(cardId, withoutLoading = false) {
      try {
        this.fetchInventoryById(cardId, withoutLoading)
      } catch (error) {
        handleApiError(error, { component: 'InventoryStore', action: 'updatedInventoryCard' })
      }
    },

    async updatedInventory(body) {
      const configStore = useConfigStore()
      const cardStore = useCardStore()

      try {
        const { data } = await apiClient().patch(`/cards/${cardStore.selectedCardData.id}`, body) 
         
        const cardData = cardStore.selectedCardData ? { ...cardStore.selectedCardData, ...data } : { ...data}
        cardStore.selectedCardData = {
          ...generateCardData(cardData, cardStore.selectedRanges, configStore.config),
          inventoryStatus: getInventoryStatus(cardData),
          listingId: cardStore.selectedCardData.listingId,
        }
      } catch (error) {
        handleApiError(error, { component: 'InventoryStore', action: 'updatedInventory' })
      }
    },

    async updateMatchedCard(selectedMatch) {
      const configStore = useConfigStore()
      const cardStore = useCardStore()

      try {
        if (!configStore.config?.hasActiveSubscription) return

        const { data } = await apiClient().post(`/cards/${cardStore.selectedCardData.id}/update-match`, { ...selectedMatch })

        if (!data) return

        cardStore.selectedCardData = generateCardData(data, cardStore.selectedRanges, configStore.config)
      } catch (error) {
        handleApiError(error, { component: 'InventoryStore', action: 'updateMatchedCard' })
      }
    },

    async fetchNextPrevIds() {
      const userStore = useUserStore()
      const cardStore = useCardStore()

      try {
        const { data } = await apiClient().get(`/inventory/${userStore.user.default_inventory_id}/prev-next/${cardStore.selectedCardData.id}`)

        if (!data) return

        const { next_card_id, prev_card_id, total_cards, current_index } = data

        this.nextCardId = next_card_id
        this.previousCardId = prev_card_id

        cardStore.totalCards = total_cards
        cardStore.cardIndex = current_index
      } catch (error) {
        handleApiError(error, { component: 'InventoryStore', action: 'fetchNextPrevIds' })
      }
    },

    async fetchDuplicates(perPage = 25) {
      const userStore = useUserStore()
      const cardStore = useCardStore()
      const configStore = useConfigStore()

      try {
        const perPage_ = getFromLocalStorageByUserId(`perPage_inventory`, userStore.user.id) || perPage

        cardStore.cardsLoading = true

        const { data } = await apiClient().get(`/inventory/${userStore.user.default_inventory_id}/duplicates?perPage=${perPage_}&page=${cardStore.page}`)
        cardStore.duplicatesLength = data.total_items

        const updatedDuplicates = []
        data.items.forEach(item => {
          updatedDuplicates.push({
            id: item.collx_match_id,
            collx_match_id: item.collx_match_id,
            isHeader: true
          })

          item.cards.forEach((item, index) => {
            const preparedCard = { 
              ...item.card, 
              ...item.listing, 
              status: item.card.status, 
              inventoryStatus: getInventoryStatus(item.card),
              set: item.card.set,
              number: item.card.number,
              title: item.card.title,
              description: item.card.description,
              player: item.card.player,
              team: item.card.team,
              year: item.card.year,
              id: item.card.id,
              sku: item.card.sku,
              listingId: item.listing?.id || null,
              collxListedStatus: item.listing?.collx_status,
              ebayListedStatus: item.listing?.ebay_status,
              whatnotListedStatus: item.listing?.whatnot_status,
              shopifyListedStatus: item.listing?.shopify_status,
              sku: item.listing?.sku || item.card?.sku,
              quantity_available: item.listing ? item.listing.quantity_available : 0,
              quantity: item.card.quantity,
            }
            
            const cardData = generateCardData(preparedCard, cardStore.selectedRanges, configStore.config)
            updatedDuplicates.push(cardData)
          })
        })

        // cardStore.pagination = {
        //   perPage: perPage_,
        //   currentPage: data.current_page,
        //   totalItems: data.total_items,
        //   nextPage: data.next_page ? data.current_page + 1 : null,
        //   prevPage: data.current_page === 1 ? null : data.current_page - 1,
        //   totalPages: data.total_pages
        // }

        cardStore.duplicates = updatedDuplicates

        cardStore.cardsLoading = false
      } catch (error) {
        handleApiError(error, { component: 'InventoryStore', action: 'fetchDuplicates' })
        cardStore.cardsLoading = false
      }
    },

    async addCardsFromBatchToInventory(batchId) {
      const cardStore = useCardStore()
      const arrayOfIds = []

      for (let i = 0; i < cardStore.selectedRanges.length; i++) {
        cardStore.cards[cardStore.selectedRanges[i].startIndex]?.id ? arrayOfIds.push(cardStore.cards[cardStore.selectedRanges[i].startIndex].id) : arrayOfIds.push(cardStore.cards[0].id)
      }

      try {
        await apiClient().post(`/inventory/add-cards`, { cards: arrayOfIds })
      } catch (error) {
        handleApiError(error, { component: 'InventoryStore', action: 'addCardsFromBatchToInventory' })
      }
    },

    async fetchViews() {
      try {
        this.viewsLoading = true
        const { data } = await apiClient().get(`/user/saved-views`)
        this.views = data?.saved_views || []
        this.viewsLoading = false

        return this.views
      } catch (error) {
        this.viewsLoading = false
        handleApiError(error, { component: 'InventoryStore', action: 'fetchViews' })
      }
    },

    async createView(body) {
      const userStore = useUserStore()
      try {
        const { data } = await apiClient().post(`/user/saved-views`, { ...body, columns: body.columns ?? [], favorite: !this.views.length ? true : false })
        this.views = data?.saved_views || this.views
        userStore.user.saved_views = data?.saved_views || userStore.user.saved_views

        return this.views[this.views.length - 1]
      } catch (error) {
        handleApiError(error, { component: 'InventoryStore', action: 'createView' })
      }
    },

    async deleteView(viewId) {
      const userStore = useUserStore()
      try {
        const { data } = await apiClient().delete(`/user/saved-views/${viewId}`)
        this.views = data?.saved_views || this.views
        userStore.user.saved_views = data?.saved_views || userStore.user.saved_views

        return this.views
      } catch (error) {
        handleApiError(error, { component: 'InventoryStore', action: 'deleteView' })
      }
    },

    async updateView(viewId, body) {
      try {
        const { data } = await apiClient().put(`/user/saved-views/${viewId}`, { ...body, columns: body.columns ?? [] })
        this.views = data?.saved_views || this.views
        // Use the updated view from the response (has id), fall back to merging id into body
        this.selectedView = data?.saved_views?.find(v => v.id === viewId) || { ...body, id: viewId }
      } catch (error) {
        handleApiError(error, { component: 'InventoryStore', action: 'updateView' })
      }
    }
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useInventoryStore, import.meta.hot))
}